class CartDeliveryOptions {
  constructor(element) {
    this.element = element;
    
    // Get form elements
    this.tabs = this.element.querySelectorAll('input[name="delivery-tab"]');
    this.panels = this.element.querySelectorAll('.delivery-panel');
    this.postcodeInput = this.element.querySelector('#cart-postcode');
    this.checkoutButton = document.querySelector('#checkout');
    
    // Get feedback elements
    this.zoneFeedback = this.element.querySelector('#zone-feedback');
    this.zoneDetectedMessage = this.element.querySelector('#zone-detected-message');
    this.zoneUnavailableMessage = this.element.querySelector('#zone-unavailable-message');
    this.checkoutErrorMessage = document.querySelector('#checkout-error-message');
    this.checkoutErrorText = document.querySelector('#checkout-error-message .error-text');
    
    // Get form fields
    this.pickupDate = this.element.querySelector('#cart-pickup-date');
    this.pickupTimeSlot = this.element.querySelector('#cart-pickup-time-slot');
    this.deliveryDate = this.element.querySelector('#cart-delivery-date');
    this.deliveryTimeSlot = this.element.querySelector('#cart-delivery-time-slot');
    this.deliveryDateField = this.element.querySelector('#delivery-date-field');
    this.deliveryTimeField = this.element.querySelector('#delivery-time-field');
    
    // Flatpickr instances
    this.pickupDatePicker = null;
    this.deliveryDatePicker = null;
    
    // Structure zone data from HTML attributes (note: hyphens in attribute names)
    this.zones = {
      zone1: {
        postcodes: this.parsePostcodes(this.element.dataset['zone-1Postcodes']),
        days: this.parseDays(this.element.dataset['zone-1Days'])
      },
      zone2: {
        postcodes: this.parsePostcodes(this.element.dataset['zone-2Postcodes']),
        days: this.parseDays(this.element.dataset['zone-2Days'])
      },
      zone3: {
        postcodes: this.parsePostcodes(this.element.dataset['zone-3Postcodes']),
        days: this.parseDays(this.element.dataset['zone-3Days'])
      }
    };
    
    // Get blackout dates
    this.blackoutDates = this.parseBlackoutDates(this.element.dataset.blackoutDates);
    
    // Minimum order settings
    this.enableMinimumOrder = this.element.dataset.enableMinimumOrder === 'true';
    this.minimumOrderAmount = parseFloat(this.element.dataset.minimumOrderAmount) || 100;
    this.cartTotal = parseFloat(this.element.dataset.cartTotal) || 0;
    
    // Current state
    this.activeTab = 'pickup';
    this.currentZone = null;
    
    this.init();
  }
  
  parsePostcodes(postcodeString) {
    if (!postcodeString || !postcodeString.trim()) {
      return [];
    }
    return postcodeString.split(',').map(p => p.trim().toLowerCase()).filter(p => p);
  }
  
  parseDays(daysString) {
    if (!daysString || !daysString.trim()) {
      return [];
    }
    return daysString.split('').map(d => parseInt(d)).filter(d => !isNaN(d));
  }
  
  parseBlackoutDates(blackoutString) {
    if (!blackoutString || !blackoutString.trim()) {
      return [];
    }
    return blackoutString.split(',').map(d => d.trim()).filter(d => d);
  }
  
  init() {
    this.initializeFlatpickr();
    this.setupEventListeners();
    this.runValidator(); // Initial validation check
  }
  
  initializeFlatpickr() {
    // Initialize pickup date picker
    if (this.pickupDate && typeof flatpickr !== 'undefined') {
      const minDate = this.pickupDate.dataset.min;
      const maxDate = this.pickupDate.dataset.max;
      
      this.pickupDatePicker = flatpickr(this.pickupDate, {
        minDate: minDate,
        maxDate: maxDate,
        disable: this.blackoutDates,
        dateFormat: 'Y-m-d',
        onChange: () => this.runValidator()
      });
    }
    
    // Initialize delivery date picker
    if (this.deliveryDate && typeof flatpickr !== 'undefined') {
      const minDate = this.deliveryDate.dataset.min;
      const maxDate = this.deliveryDate.dataset.max;
      
      this.deliveryDatePicker = flatpickr(this.deliveryDate, {
        minDate: minDate,
        maxDate: maxDate,
        disable: this.blackoutDates,
        dateFormat: 'Y-m-d',
        onChange: () => this.runValidator()
      });
    }
  }
  
  setupEventListeners() {
    // Tab switching - using radio inputs
    this.tabs.forEach(tab => {
      tab.addEventListener('change', (e) => {
        this.switchTab(e.target.value);
        this.runValidator();
      });
    });
    
    // Postcode input
    if (this.postcodeInput) {
      this.postcodeInput.addEventListener('input', () => {
        this.runValidator();
      });
    }
    
    // All form fields
    const formFields = [
      this.pickupDate,
      this.pickupTimeSlot, 
      this.deliveryDate,
      this.deliveryTimeSlot
    ];
    
    formFields.forEach(field => {
      if (field) {
        field.addEventListener('change', () => {
          this.runValidator();
        });
      }
    });
  }
  
  switchTab(tabName) {
    this.activeTab = tabName;
    
    // Radio inputs handle their own checked state automatically
    // Just update panel visibility
    this.panels.forEach(panel => {
      panel.classList.remove('delivery-panel--active');
    });
    this.element.querySelector(`[data-panel="${tabName}"]`).classList.add('delivery-panel--active');
    
    // Show/hide delivery date and time fields individually
    if (this.deliveryDateField) {
      this.deliveryDateField.style.display = tabName === 'delivery' ? 'none' : 'none'; // Initially hidden until postcode validated
    }
    if (this.deliveryTimeField) {
      this.deliveryTimeField.style.display = tabName === 'delivery' ? 'none' : 'none'; // Initially hidden until postcode validated
    }
  }
  
  runValidator() {
    let isValid = false;
    let errorMessage = '';
    
    // Check minimum order amount first (if enabled)
    if (this.enableMinimumOrder && this.cartTotal < this.minimumOrderAmount) {
      isValid = false;
      errorMessage = `Minimum order of $${this.minimumOrderAmount.toFixed(2)} required for checkout`;
      this.updateCheckoutButton(isValid, errorMessage);
      return;
    }
    
    if (this.activeTab === 'pickup') {
      isValid = this.validatePickupFields();
      if (!isValid) {
        if (!this.pickupDate?.value?.trim()) {
          errorMessage = 'Please select a pickup date to continue';
        } else if (!this.pickupTimeSlot?.value?.trim()) {
          errorMessage = 'Please select a pickup time slot to continue';
        } else {
          errorMessage = 'Please complete all pickup details to continue';
        }
      }
      
    } else if (this.activeTab === 'delivery') {
      const postcodeValid = this.validatePostcode();
      const fieldsValid = this.validateDeliveryFields();
      const dateValid = this.validateDeliveryDate();
      
      isValid = postcodeValid && fieldsValid && dateValid;
      
      if (!postcodeValid) {
        const postcode = this.postcodeInput?.value?.trim();
        if (!postcode) {
          errorMessage = 'Please enter your postcode to check delivery availability';
        } else {
          errorMessage = 'This postcode is not available for delivery';
        }
      } else if (!fieldsValid) {
        if (!this.deliveryDate?.value?.trim()) {
          errorMessage = 'Please select a delivery date to continue';
        } else if (!this.deliveryTimeSlot?.value?.trim()) {
          errorMessage = 'Please select a delivery time slot to continue';
        } else {
          errorMessage = 'Please complete all delivery details to continue';
        }
      } else if (!dateValid) {
        errorMessage = 'Selected date is not available for delivery in your area';
      }
    }
    
    this.updateCheckoutButton(isValid, errorMessage);
  }
  
  validatePickupFields() {
    return this.pickupDate?.value?.trim() && this.pickupTimeSlot?.value?.trim();
  }
  
  validateDeliveryFields() {
    return this.deliveryDate?.value?.trim() && this.deliveryTimeSlot?.value?.trim();
  }
  
  validatePostcode() {
    const postcode = this.postcodeInput?.value?.trim();
    
    if (!postcode) {
      // Hide date and time fields
      if (this.deliveryDateField) this.deliveryDateField.style.display = 'none';
      if (this.deliveryTimeField) this.deliveryTimeField.style.display = 'none';
      return false;
    }
    
    const cleanPostcode = postcode.toLowerCase();
    
    // Find matching zone
    for (const [zoneKey, zoneData] of Object.entries(this.zones)) {
      if (zoneData.postcodes.length > 0) {
        for (const pc of zoneData.postcodes) {
          if (cleanPostcode.startsWith(pc)) {
            this.currentZone = zoneKey;
            this.updateZoneFeedback(''); // Pass empty string to hide all feedback
            this.updateDatePickerForZone(zoneKey);
            // Show date and time fields
            if (this.deliveryDateField) this.deliveryDateField.style.display = 'flex';
            if (this.deliveryTimeField) this.deliveryTimeField.style.display = 'flex';
            return true;
          }
        }
      }
    }
    
    this.currentZone = null;
    this.updateZoneFeedback('This postcode is not available for delivery');
    // Hide date and time fields
    if (this.deliveryDateField) this.deliveryDateField.style.display = 'none';
    if (this.deliveryTimeField) this.deliveryTimeField.style.display = 'none';
    return false;
  }
  
  validateDeliveryDate() {
    if (!this.currentZone || !this.deliveryDate?.value) return true; // Skip if no zone or date
    
    const selectedDate = new Date(this.deliveryDate.value);
    const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const zoneDays = this.zones[this.currentZone].days;
    
    // Check if selected day is allowed for this zone
    const isValidForZone = zoneDays.includes(dayOfWeek);
    
    // Check if date is in blackout dates
    const dateString = this.deliveryDate.value; // Already in YYYY-MM-DD format
    const isBlackedOut = this.blackoutDates.includes(dateString);
    
    return isValidForZone && !isBlackedOut;
  }
  
  updateZoneFeedback(message) {
    if (!this.zoneFeedback) return;
    
    if (message.includes('not available')) {
      this.zoneFeedback.style.display = 'block';
      this.zoneDetectedMessage.style.display = 'none';
      this.zoneUnavailableMessage.style.display = 'block';
    } else {
      // Zone detected successfully or empty message - hide all feedback messages
      this.zoneFeedback.style.display = 'none';
      this.zoneDetectedMessage.style.display = 'none';
      this.zoneUnavailableMessage.style.display = 'none';
    }
  }
  
  updateDatePickerForZone(zoneKey) {
    if (!this.deliveryDatePicker) return;
    
    const zoneData = this.zones[zoneKey];
    const availableDays = zoneData.days;
    
    // Create disable function for unavailable days
    const disableDays = (date) => {
      const dayOfWeek = date.getDay();
      return !availableDays.includes(dayOfWeek);
    };
    
    // Combine blackout dates with unavailable days
    const disableOptions = [
      ...this.blackoutDates, // Specific blackout dates
      disableDays // Function to disable days of week
    ];
    
    // Update Flatpickr configuration
    this.deliveryDatePicker.set('disable', disableOptions);
  }
  
  updateCheckoutButton(isValid, errorMessage) {
    if (!this.checkoutButton) return;
    
    if (isValid) {
      this.checkoutButton.disabled = false;
      this.checkoutButton.style.opacity = '1';
      this.checkoutButton.style.pointerEvents = 'auto';
      
      // Hide error message
      if (this.checkoutErrorMessage) {
        this.checkoutErrorMessage.style.display = 'none';
      }
      
      // Remove click prevention
      this.checkoutButton.onclick = null;
    } else {
      this.checkoutButton.disabled = true;
      this.checkoutButton.style.opacity = '0.5';
      this.checkoutButton.style.pointerEvents = 'none';
      
      // Show error message
      if (this.checkoutErrorMessage && this.checkoutErrorText) {
        this.checkoutErrorText.textContent = errorMessage;
        this.checkoutErrorMessage.style.display = 'block';
      }
      
      // Add click prevention with error message
      this.checkoutButton.onclick = (e) => {
        e.preventDefault();
        alert(errorMessage);
        return false;
      };
    }
  }
}

// Initialize CartDeliveryOptions on page load
document.addEventListener('DOMContentLoaded', () => {
  const deliveryOptionsElement = document.querySelector('.cart-delivery-options');
  if (deliveryOptionsElement) {
    new CartDeliveryOptions(deliveryOptionsElement);
  }
});
